/**
 * TerrainProfileSampler.ts
 *
 * TerrainProfileSampler — walk a uv path at regular intervals and sample elevation.
 */

import type { TerrainLevel } from "@/terrain/terrainTypes";
import type { TerrainService } from "@/terrain/TerrainService";

export type ProfilePoint = {
  distance: number;
  u: number;
  v: number;
  elevation: number | null;
};

/**
 * Sample elevation along a polyline path at regular spacing.
 *
 * @param service  TerrainService for elevation queries
 * @param path     Array of [u, v] waypoints
 * @param spacing  Sample spacing in meters along the path
 * @param level    Optional pyramid level to use
 */
export function sampleProfile(
  service: Pick<TerrainService, "getElevation">,
  path: Array<[number, number]>,
  spacing: number,
  level?: TerrainLevel
): ProfilePoint[] {
  if (path.length < 2) {
    return [];
  }

  const result: ProfilePoint[] = [];
  let totalDistance = 0;
  let nextSampleAt = 0;
  let [segStart] = path;

  // Sample at path start
  result.push({
    distance: 0,
    u: segStart[0],
    v: segStart[1],
    elevation: service.getElevation(segStart[0], segStart[1], { level }),
  });
  nextSampleAt = spacing;

  for (let i = 1; i < path.length; i++) {
    const segEnd = path[i];
    const dx = segEnd[0] - segStart[0];
    const dy = segEnd[1] - segStart[1];
    const segLength = Math.sqrt(dx * dx + dy * dy);

    if (segLength < 1e-10) {
      segStart = segEnd;
      continue;
    }

    const nx = dx / segLength;
    const ny = dy / segLength;
    let distIntoSegment = nextSampleAt - totalDistance;

    while (distIntoSegment <= segLength) {
      const mx = segStart[0] + nx * distIntoSegment;
      const my = segStart[1] + ny * distIntoSegment;
      result.push({
        distance: nextSampleAt,
        u: mx,
        v: my,
        elevation: service.getElevation(mx, my, { level }),
      });
      nextSampleAt += spacing;
      distIntoSegment = nextSampleAt - totalDistance;
    }

    totalDistance += segLength;
    segStart = segEnd;
  }

  // Sample at path end if not already sampled
  const lastWaypoint = path[path.length - 1];
  const lastSampled = result[result.length - 1];
  if (
    lastSampled &&
    (Math.abs(lastSampled.u - lastWaypoint[0]) > 0.01 ||
      Math.abs(lastSampled.v - lastWaypoint[1]) > 0.01)
  ) {
    result.push({
      distance: totalDistance,
      u: lastWaypoint[0],
      v: lastWaypoint[1],
      elevation: service.getElevation(lastWaypoint[0], lastWaypoint[1], {
        level,
      }),
    });
  }

  return result;
}
